/**
 * Mock data for the offchain tutorial board.
 *
 * The tutorial mounts the real `LobbyClient` against this synthetic board so
 * every panel/canvas/drawer renders live UI, but every value flows from
 * `TutorialContext` instead of the indexer or wallet.
 */

import type { SeatData } from '@/components/grid';
import type { LobbyAdapter } from '@/features/lobby/data/mock-adapters';
import type {
  V2GridRewardEvent,
  V2GridSeatEvent,
} from '@/lib/types/v2';
import { generateGridAvatarUrl } from '@/lib/avatar';
import { FAKE_HOLDERS } from '@/components/tutorial/constants';

/** Stable id used for the synthetic tutorial board everywhere it's rendered. */
export const TUTORIAL_BOARD_ID = 'tutorial';

/** Top-left seat — always the cheapest "floor" seat the tutorial walks the user toward. */
export const TUTORIAL_FLOOR_INDEX = 0;

/** The address that represents the player while they're in tutorial mode. */
export const TUTORIAL_VIEWER = {
  address: '0x707010A100000000000000000000000000000001',
  name: 'You',
  avatarUrl: generateGridAvatarUrl('0x707010A100000000000000000000000000000001', 96),
} as const;

/** Address used when "competitors" buy out neighbouring seats during the late tutorial steps. */
export const TUTORIAL_RAIDER = {
  address: '0xC0FFEE0B100000000000000000000000000000A1',
  name: 'raider.eth',
  avatarUrl: generateGridAvatarUrl('0xC0FFEE0B100000000000000000000000000000A1', 96),
} as const;

/** 10x10 = 100 tiles. Tutorial uses a small grid so the floor/viewer tile is unmistakable. */
export const TUTORIAL_GRID_SIZE = 10;
export const TUTORIAL_TOTAL_SEATS = TUTORIAL_GRID_SIZE * TUTORIAL_GRID_SIZE;

/** Decimals for the tutorial's tax token. We use USDC (6 dp) for an approachable dollar narrative. */
export const TUTORIAL_TAX_TOKEN_DECIMALS = 6;
const TOKEN_UNIT = 10n ** BigInt(TUTORIAL_TAX_TOKEN_DECIMALS);
function dollars(amount: number): bigint {
  return BigInt(Math.round(amount * Number(TOKEN_UNIT)));
}

/** Floor price the user is led to take over (USDC). */
export const TUTORIAL_FLOOR_PRICE_USD = 5;
export const TUTORIAL_FLOOR_PRICE = dollars(TUTORIAL_FLOOR_PRICE_USD);

/** Multiplier applied during the price-set step so the user's listing is clearly above floor. */
export const TUTORIAL_VIEWER_PRICE_MULTIPLIER = 1.2;
export const TUTORIAL_VIEWER_PRICE_USD =
  TUTORIAL_FLOOR_PRICE_USD * TUTORIAL_VIEWER_PRICE_MULTIPLIER;
export const TUTORIAL_VIEWER_PRICE = dollars(TUTORIAL_VIEWER_PRICE_USD);

/** "Defend" path target — 2x the original price so weekly rent is meaningful but not punishing. */
export const TUTORIAL_DEFEND_PRICE_USD = TUTORIAL_VIEWER_PRICE_USD * 2;
export const TUTORIAL_DEFEND_PRICE = dollars(TUTORIAL_DEFEND_PRICE_USD);

const SECONDS_PER_DAY = 86_400;

/** Indexes the script bumps during the competitor-buyouts wave.
 *  Each is initialised in the cheap band ($5.05..$5.95) so that
 *  pre-wave the viewer's $6 listing sits at sorted rank 9 (10th from
 *  cheapest). The user asked the wave to fire as a 5-tile cascade
 *  (down from the previous 10) and still land the viewer "close to
 *  being bought out" by the time the defend/exit choice card shows
 *  — with 9 cheap-band tiles below the viewer pre-wave, sweeping
 *  5 of them above the viewer's listing leaves exactly 4 cheaper
 *  tiles, i.e. sorted rank 4 (position 5 from cheapest). Same
 *  "approaching the floor" payoff as the old 10-tile cascade, just
 *  paced tighter. */
export const TUTORIAL_COMPETITOR_INDEXES = [1, 2, 3, 4, 5] as const;

/** Indexes the defend wave bumps after the user picks "Defend".
 *  Pre-wave the viewer is at $12 with 8 tiles cheaper (4 cheap-band
 *  survivors at indexes 6..9 + 4 wave-1 bumped tiles still under
 *  $12 at indexes 1..4 — index 5 was wave-1's "above-defend" bump
 *  that pushed it past $12 by design, see `runCompetitorBuyouts`).
 *  The wave bumps these 5 indexes above $12 so the viewer ends up
 *  with only 3 cheaper neighbours — sorted rank 3 (position 4 from
 *  cheapest), the "made it close" payoff the user described. We
 *  sweep the 4 cheap-band survivors first (visually the floor
 *  walking up toward the viewer) then a single wave-1 leftover for
 *  the 5th crumble. */
export const TUTORIAL_DEFEND_WAVE_INDEXES = [6, 7, 8, 9, 1] as const;

/** Indexes the exit wave bumps after the user picks "Exit". Pre-wave
 *  the viewer is at sorted rank 4 (position 5) with 4 cheaper tiles
 *  in front of them — the cheap-band survivors at indexes 6..9.
 *  The wave bumps those 4 in succession and lands the 5th crumble on
 *  the viewer's own tile (index 0), so the cascade visually walks
 *  the floor up to and including the viewer. */
export const TUTORIAL_EXIT_WAVE_INDEXES = [6, 7, 8, 9, TUTORIAL_FLOOR_INDEX] as const;

/** Number of seats whose initial price sits in the [$5.01, $6.00] band.
 *  The floor seat itself is excluded — it stays exactly at the floor
 *  price so the buyout drawer math reads cleanly. With 9 cheap-band
 *  tiles below the viewer's $6 listing, the 5-tile competitor wave
 *  leaves 4 cheaper neighbours (rank 4 / position 5) — the
 *  "approaching the floor" payoff before the defend/exit choice. */
const CHEAP_BAND_COUNT = 9;

function priceLadder(): number[] {
  // Hand-tuned price ladder.
  //
  // - Floor seat (index 0) sits exactly at `TUTORIAL_FLOOR_PRICE_USD`
  //   ($5) so the buyout drawer's cost line matches the script copy.
  // - The next `CHEAP_BAND_COUNT` (9) seats are spread evenly across
  //   the (5.00, 6.00) band so post-take-over the viewer's $6
  //   listing ranks 10th from the cheapest (9 seats cheaper than
  //   them). Spreading evenly avoids a perfectly stacked column the
  //   eye could read as "everything is the same price".
  // - All remaining seats sit *strictly above $12* (the viewer's
  //   defend price). This keeps the rank math clean for the
  //   competitor-buyouts → defend-wave story (5-tile waves):
  //     • after `competitor-buyouts` the viewer is at sorted rank
  //       4 (position 5) because exactly the 4 cheap-band survivors
  //       (indexes 6..9) sit below their $6 listing — no high-band
  //       tiles dip below $6 either, which the previous wobble
  //       (anchored at $6.10 with ±1 amplitude) used to do (it
  //       produced $5.52 at i=15 in the old layout, polluting the
  //       cheaper-than-viewer set).
  //     • after `path-defend` the viewer is at sorted rank 8
  //       (position 9): 4 cheap-band survivors + 4 wave-1 bumped
  //       (under $12) cheaper. The 5th wave-1 bump and *every*
  //       high-band tile sit above $12 by design.
  //     • after the defend wave the viewer is at sorted rank 3
  //       (position 4): 5 of the 8 cheaper tiles get bumped above
  //       $12, leaving exactly 3 cheaper neighbours.
  //
  //   Anchoring the ramp at $13.50 and clamping the wobble to a
  //   non-negative offset means every high-band price ≥ $13.50, so
  //   the user *cannot* see a high-band tile in the "cheaper than
  //   viewer" sort slots at any point in the script — the cheap
  //   band is the only source of cheaper tiles, and we control its
  //   composition exactly via the wave-1 / wave-2 indexes.
  const out: number[] = new Array(TUTORIAL_TOTAL_SEATS);
  out[TUTORIAL_FLOOR_INDEX] = TUTORIAL_FLOOR_PRICE_USD;
  for (let i = 1; i <= CHEAP_BAND_COUNT; i++) {
    // 9 seats stepping 5.10, 5.20, 5.30, … up to ~5.95.
    const t = (i - 0.5) / CHEAP_BAND_COUNT; // 0..1, midpoint-shifted
    out[i] = +(TUTORIAL_FLOOR_PRICE_USD + 0.05 + t * 0.9).toFixed(2);
  }
  for (let i = CHEAP_BAND_COUNT + 1; i < TUTORIAL_TOTAL_SEATS; i++) {
    // Ramp + non-negative wobble. We use `|sin|` and `|cos|` so the
    // wobble only pushes prices *upward* from the ramp — the
    // previous signed wobble produced `ramp - 1.0` at i=15
    // ($5.52), which polluted the cheaper-than-viewer set the
    // tutorial relies on. Clamping to `Math.abs` preserves the
    // organic per-tile variation without creating dips.
    const ramp = 13.5 + (i - (CHEAP_BAND_COUNT + 1)) * 0.6;
    const wobble = Math.abs(Math.sin(i * 1.13)) * 0.6 + Math.abs(Math.cos(i * 0.71)) * 0.4;
    out[i] = +(ramp + wobble).toFixed(2);
  }
  return out;
}

interface HolderSlot {
  address: string;
  name: string;
  level: number;
  avatarUrl: string;
}

function buildHolderRoster(): HolderSlot[] {
  // Grab ~50 unique avatars from FAKE_HOLDERS, then repeat ~30 of them so
  // the 100-seat grid feels populated by ~50 unique players with some
  // recognisable repeats (multiple seats per power player).
  const unique = FAKE_HOLDERS.slice(0, 50).map((h, i) => ({
    address: h.address,
    name: h.name,
    avatarUrl: generateGridAvatarUrl(h.address, 96),
    level: 1 + (i % 6),
  }));
  const repeats: HolderSlot[] = [];
  for (let i = 0; i < 50; i++) {
    repeats.push(unique[i % unique.length]);
  }
  // First batch fills "primary" slots, second batch fills the rest. The
  // exact layout is not load-bearing — the tutorial walks the player
  // tile-by-tile around the floor.
  return [...unique, ...repeats];
}

export function buildTutorialSeats(): SeatData[] {
  const ladder = priceLadder();
  const roster = buildHolderRoster();
  const now = Math.floor(Date.now() / 1000);

  const seats: SeatData[] = [];
  for (let i = 0; i < TUTORIAL_TOTAL_SEATS; i++) {
    const holder = roster[i % roster.length];
    const usd = ladder[i];
    const price = dollars(usd);
    const heldDays = ((i * 7) % 21) + 3;
    seats.push({
      seatIndex: i,
      holder: holder.address,
      holderAvatar: holder.avatarUrl,
      holderName: holder.name,
      holderLevel: holder.level,
      selfAssessedPrice: price,
      balance: price * 4n,
      runwaySeconds: heldDays * SECONDS_PER_DAY,
      lastUpdateTime: now - heldDays * SECONDS_PER_DAY,
      claimedAt: now - heldDays * SECONDS_PER_DAY,
      lastPriceChangeTime: now - heldDays * SECONDS_PER_DAY,
      createdAt: now - heldDays * SECONDS_PER_DAY,
      isOwn: false,
      isForfeited: false,
    });
  }

  return seats;
}

/** Adapters shown in the tutorial yield panel. Three dummy entries — enough variety to
 *  highlight the panel without overwhelming the user. */
export function buildTutorialAdapters(): LobbyAdapter[] {
  return [
    {
      id: 'tutorial.uniswap-v3',
      kind: 'uniswap-v3',
      label: 'Uniswap v3 LP',
      subtitle: 'USDC / WETH LP fees → seat deposits',
      status: 'live',
      pair: 'USDC / WETH',
      feeBps: 500,
      isFullRange: true,
      tvlUsd: '$214k',
      apr: '38.4%',
      accrued: '12.40',
      accruedUsd: '$12.40',
      links: [
        { href: '#', label: 'Uniswap pool', icon: 'uniswap' },
        { href: '#', label: 'Dexscreener', icon: 'dexscreener' },
      ],
    },
    {
      id: 'tutorial.flaunch',
      kind: 'flaunch',
      label: 'TUTOR coin',
      subtitle: 'Flaunch token',
      status: 'live',
      coinSymbol: 'TUTOR',
      coinAddress: '0xTUT0Rcoin000000000000000000000000000000001',
      tvlUsd: '$48.6k',
      apr: '24.1%',
      accrued: '3.10',
      accruedUsd: '$3.10',
      links: [{ href: '#', label: 'Flaunch', icon: 'flaunch' }],
    },
    {
      // Per the latest tutorial copy ("Takeover protocol fees" replaces
      // the third-party Aerodrome gauge example), the third adapter
      // surfaces the protocol's own buyout fees as a yield source.
      // Modelled as a `custom` adapter so the panel renders the
      // contract identity + headline metric layout rather than the
      // gauge-weight specifics.
      id: 'tutorial.protocol-fees',
      kind: 'custom',
      label: 'Takeover protocol fees',
      subtitle: 'Buyout fees → seat rewards',
      status: 'live',
      contractName: 'Takeover protocol fees',
      contractAddress: '0xFEE5000000000000000000000000000000000001',
      headlineMetric: 'Net buyout fees \u2192 distributed weekly',
      tvlUsd: '$96.2k',
      apr: '17.8%',
      accrued: '0.85',
      accruedUsd: '$0.85',
      links: [{ href: '#', label: 'Protocol contract', icon: 'globe' }],
    },
  ];
}

/** Generate a fresh reward event for the streaming step. `usd` returned
 *  alongside so the context can keep a running claimable total in sync
 *  with what the panel shows.
 *
 *  An optional `usdOverride` lets specific steps push amounts that fall
 *  outside the default $0.40–$1.80 ambient band — `competitor-buyouts`
 *  uses this to spam the rewards panel with $5–$25 chunks so the
 *  "board is heating up" beat reads as visibly more lucrative than the
 *  early `rewards-stream` ambient drip. */
export function makeTutorialRewardEvent(
  seq: number,
  usdOverride?: number,
): {
  event: V2GridRewardEvent;
  usd: number;
} {
  const adapterPool = ['uniswap-v3', 'flaunch', 'aerodrome'] as const;
  const adapterType = adapterPool[seq % adapterPool.length];
  const usd = usdOverride ?? 0.4 + (seq % 5) * 0.35; // default 0.40 – 1.80
  // `RewardsTab` expects amounts in 18-dp USD, see `rewardEventsUsd` in
  // `useGridPage`. We bypass that derivation here by writing the amount
  // directly in 1e18 scale.
  const amountUsdScaled = BigInt(Math.round(usd * 1e18));
  const ts = Math.floor(Date.now() / 1000) - seq;
  const id = `tut-reward-${seq}-${ts}`;
  const event: V2GridRewardEvent = {
    id,
    gridId: TUTORIAL_BOARD_ID,
    assetHash: null,
    adapterType,
    payee: '0x0000000000000000000000000000000000000000',
    token: '0x0000000000000000000000000000000000000000',
    amount: amountUsdScaled.toString(),
    amountUsd: amountUsdScaled.toString(),
    timestamp: ts,
    blockNumber: 0,
    txHash: `0xtutorial${seq.toString(16).padStart(8, '0')}`,
  };
  return { event, usd };
}

/** Generate a buyout sale event for the late-tutorial competitor wave. */
export function makeTutorialSaleEvent(args: {
  seatIndex: number;
  buyer: string;
  oldHolder: string;
  price: bigint;
  seq: number;
}): V2GridSeatEvent {
  const ts = Math.floor(Date.now() / 1000) - args.seq;
  return {
    id: `tut-sale-${args.seatIndex}-${ts}`,
    seatId: `${TUTORIAL_BOARD_ID}-${args.seatIndex}`,
    gridId: TUTORIAL_BOARD_ID,
    eventType: 'BOUGHT_OUT',
    user: args.buyer as `0x${string}`,
    timestamp: ts,
    blockNumber: 0,
    txHash: `0xtutorialsale${args.seq.toString(16).padStart(8, '0')}`,
    price: args.price.toString(),
    newPrice: args.price.toString(),
    oldHolder: args.oldHolder as `0x${string}`,
  };
}
